import React, { useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { Grid, FormControlLabel, TextField, Button, InputAdornment, IconButton, Box } from "@material-ui/core";
import { ArrowDropUp, ArrowDropDown } from "@material-ui/icons";
import Pagination from "@material-ui/lab/Pagination";
import Skeleton from "@material-ui/lab/Skeleton";
import { useStyles as reportQueueAdminStyles } from "./ReportQueueAdminManagement.styles";
import { useStyles as reportQueueStyles } from "../ReportQueueManagement/ReportQueueManagement.styles";
import ReportQueueAdminTable from "./ReportQueueAdminTable";
import clsx from "clsx";
import apiClient from "../../auth/apiClient";
import ReportQueueService from "../../services/ReportQueueService";
import { useEffect } from "react";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import { useSelector, shallowEqual } from "react-redux";
import { FindEntity } from "../../state/slices/entities";
import { selectParentEntity } from "../../state/slices/CoreEntity";
import { useCoreEntityContext } from "../../hooks/useCoreEntitySlice";
import { ENTITY_TYPE } from "../../constants";
import moment from "moment";
import useHubContext from "../../hooks/useHubContext";
import { REPORT_QUEUE_STATUS_CODES } from "../../constants";

const STATUS_MAP = {
  REPORT_QUEUE_PROCESSING: REPORT_QUEUE_STATUS_CODES.PROCESSING,
  REPORT_QUEUE_ABANDONED: REPORT_QUEUE_STATUS_CODES.ABANDONED,
  REPORT_QUEUE_COMPLETED: REPORT_QUEUE_STATUS_CODES.COMPLETED,
};

const ReportQueueAdminManagement = forwardRef(({updatePauseStatus} , ref) => {
  const classes = { ...reportQueueAdminStyles(), ...reportQueueStyles() };
  const enqueueSnackbar = useEnqueueSnackbar();

  const { Connected: PortalHubConnected, portalHub } = useHubContext();

  const reportQueueService = new ReportQueueService(apiClient);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleting, setDeleting] = useState({});
  const [value, setValue] = useState(1);
  const [isQueuePaused, setIsQueuePaused] = useState(false);

  const itemLimit = 10;

  const useCoreEntitySlice = useCoreEntityContext();

  const entityID = useSelector((state) =>
      useCoreEntitySlice
        ? state.coreEntities.ContextID
        : state.entities?.ContextID
    );
  
    const parentEntity = useSelector((state) => {
      if (useCoreEntitySlice) return selectParentEntity(entityID)(state);
  
      const entity = FindEntity(state.entities?.EntityList ?? [], entityID);
      return FindEntity(state.entities?.EntityList ?? [], entity.parententityid);
    }, shallowEqual);

  const fetchActivityData = async () => {
    setLoading(true);
    try {
      const response = await reportQueueService.getAllQueueProcessingReports();
      const sorted = response.data?.reports?.sort((a, b) =>
        moment(b.rqCreatedTime).valueOf() - moment(a.rqCreatedTime).valueOf()
      );
      setResults(sorted);
      setTotalCount(sorted.length);
      if(response.data?.threadCount) {
        const paused = response.data.threadCount.IsPaused;        
        setValue(response.data.threadCount.LimitTaskCount);
        setIsQueuePaused(paused);
        updatePauseStatus(paused);
      }
    } catch (error) {
      console.error("Error fetching report queue items:", error);
      enqueueSnackbar("Error fetching report queue items.", {
        variant: "error",
        tag: "FailedToLoadReportJob",
      });
    } finally {
      setLoading(false);
    }
  }

  const insertSorted = (arr, item) => {
    return [...arr, item].sort(
      (a, b) =>
        moment(b.rqCreatedTime).valueOf() - moment(a.rqCreatedTime).valueOf()
    );
  };

  const normalizeQueueItem = (msg) => {
    if (!msg) return null;

    if (msg.rqId) return msg;

    const q = msg.queue;
    return {
      facilityId: q.FacilityId,
      rqStatusId: q.RqStatusId,
      rqProcessorId: q.RqProcessorId,
      userId: q.UserId,
      userName: msg.UserName,
      reportParameters: (() => {
        try {
          return JSON.parse(q.ReportParameters);
        } catch {
          return q.ReportParameters;
        }
      })(),
      rqStoragePath: q.RqStoragePath,
      reportId: q.ReportId,
      scheduledReportId: q.ScheduledReportId,
      rqCompletionTime: q.RqCompletionTime,
      rqId: q.RqId,
      rqCreatedTime: q.RqCreatedTime,
      rqPickupTime: q.RqPickupTime,
      version: q.Version,
    };
  };

  const handleHubMessage = useCallback((rawMessage, fallbackStatus) => {
      try {
        const outer = JSON.parse(rawMessage);
        const topic = outer.Topic || fallbackStatus;
        const inner = JSON.parse(outer.Message);
        const rqid = inner.RqId || inner.queue?.RqId;
  
        const updateState = (prev) => {
          switch (topic) {
            case "REPORT_QUEUE_ADDED": {
              const normalized = normalizeQueueItem(inner);
              if (!normalized) return prev;
              if (prev.some((item) => item.rqId === normalized.rqId)) return prev;
              setTotalCount((prev) => prev + 1);
              return insertSorted(prev, normalized);
            }
            case "REPORT_QUEUE_PROCESSING":
              return prev.map((item) =>
                item.rqId === rqid
                  ? { ...item, ...inner, rqStatusId: STATUS_MAP[topic] }
                  : item
              );
            case "REPORT_QUEUE_CANCELED":
            case "REPORT_QUEUE_ABANDONED":
            case "REPORT_QUEUE_COMPLETED":
              setTotalCount((prev) => Math.max(0, prev - 1));
              return prev.filter((item) => item.rqId !== rqid);
            default:
              console.warn("Unhandled ReportQueue topic:", topic);
              return prev;
          }
        };
        setResults(updateState);
      } catch (err) {
        console.error("Failed to handle report queue message:", err, rawMessage);
      }
    }, []);

  useEffect(() => {
    fetchActivityData();
  }, [])

  useEffect(() => {
      if (!entityID || !PortalHubConnected) return;
  
      const topics = Object.keys(STATUS_MAP).concat([
        "REPORT_QUEUE_ADDED",
        "REPORT_QUEUE_CANCELED",
      ]);
  
      topics.forEach((topic) => {
        portalHub.subscribe(topic, (msg) =>
          handleHubMessage(msg, topic)
        );
      });
  
      return () => {
        topics.forEach((topic) => portalHub.unsubscribe(topic));
      };
    }, [entityID, PortalHubConnected, portalHub, handleHubMessage]);

  const handleCancel = async (id) => {
    const facilityId = parentEntity.typeid === ENTITY_TYPE.FacilityGroup ? parentEntity.entityid : entityID;
    try {
      const params = {
        rqId: id,
        RqStatusId: 61,
        entityId: facilityId,
      };
      await reportQueueService.cancelReport(params);
      setDeleting((prev) => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setResults((prev) => prev.filter((r) => r.rqId !== id));
        setTotalCount((prev) => Math.max(0, prev - 1));
        setCurrentPage(1);
        setDeleting((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
      }, 500);
      enqueueSnackbar("Report job cancelled successfully.", {
        variant: "success",
        tag: "ReportJobCancelled",
      });
    } catch {
      enqueueSnackbar("Unable to cancel the report job, please try again later.", {
        variant: "error",
        tag: "FailedToCancelReportJob",
      });
    }
  }

  const handleUpdate = async (isPause) => {
    let errorMessage = "";
    let tag = "";
    try {
      let threadCountToUpdate;
      let successMessage = "";

      if (isPause) {
        threadCountToUpdate = 0;
        successMessage = "Report Queues paused successfully.";
        errorMessage = "Error in pausing report queues.";
        tag = "QueuesPaused";
      } else if (isPause === false) {
        threadCountToUpdate = value;
        successMessage = "Report Queues unpaused successfully.";
        errorMessage = "Error in unpausing report queues.";
        tag = "QueuesUnpaused";
      } else {
        if (value < 1 || value > 10) return;
        threadCountToUpdate = value;
        successMessage = "Thread count updated successfully.";
        errorMessage = "Unable to update thread count, please try again later.";
        tag = "ThreadCountUpdated";
      }

      await reportQueueService.updateThreadCount(threadCountToUpdate);

      enqueueSnackbar(successMessage, {
        variant: "success",
        tag,
      });

      if (typeof isPause === "boolean") {
        setIsQueuePaused(isPause);
        updatePauseStatus(isPause);
      }

    } catch (err) {
      console.error(err);
      enqueueSnackbar(errorMessage, {
        variant: "error",
        tag,
      });
    }
  };

  const clamp = (num) => Math.min(10, Math.max(1, num));

  const handleChange = (e) => {
    const num = parseInt(e.target.value, 10);
    if (!isNaN(num)) setValue(clamp(num));
    else setValue("");
  };

  const handleBlur = () => {
    if (value === "" || isNaN(value)) {
      setValue(1);
    }
  };

  const step = (delta) => setValue((v) => clamp((v || 1) + delta));

  useImperativeHandle(ref, () => ({
    isQueuePaused,
    handleUpdate
  }));

  return (
    <Grid container className={clsx(classes.infoContainer)}>
      <Grid container item xs={12} className={clsx(classes.header)}>
        <Grid container item xs={12} md={6} spacing={1} alignItems="center">
          <Grid item className={clsx(classes.threadInput)}>
            <FormControlLabel
              control={
                <TextField
                  variant="outlined"
                  size="small"
                  value={value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isQueuePaused}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className={clsx(classes.iconContainer)}>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <IconButton
                            size="small"
                            aria-label="increase"
                            disabled={isQueuePaused}
                            onClick={() => step(1)}
                            tabIndex={-1}
                            className={clsx(classes.iconButton)}
                          >
                            <ArrowDropUp />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="decrease"
                            disabled={isQueuePaused}
                            onClick={() => step(-1)}
                            tabIndex={-1}
                            className={clsx(classes.iconButton)}
                          >
                            <ArrowDropDown />
                          </IconButton>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  className={clsx(classes.threadsInput)}
                />  
              }
              label={"Threads Per Pod"}
              labelPlacement="start"
            />
          </Grid>
          <Grid item className={clsx(classes.updateButton)}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={isQueuePaused}
              onClick={() => handleUpdate()}
            >
              Update
            </Button>
          </Grid>
          </Grid>
          
      </Grid>
      <Grid item xs={12}>
        {loading ? (
          <div className="skeleton-container">
            {[...Array(3)].map((_, index) => (
              <Skeleton
                key={index}
                className={classes.skeleton}
                animation="wave"
              />
            ))}
          </div>
        ) : (
          <>
            {results?.length === 0 && (
              <div
                className={clsx([
                  "animate__animated",
                  "animate__flipInX",
                  "results",
                  "not-found",
                  classes.noResults,
                ])}
              >
                <span className={clsx(["no-results-text"])}>
                  No report queue records
                </span>
              </div>
            )}
            {results?.length > 0 && (
              <Grid
                container
                item
                spacing={2}
                xs={12}
                className={clsx(classes.tableContainer)}
              >
                <Grid container item xs={12} className={clsx(classes.paginationContainer)}>
                  {Math.ceil(totalCount / itemLimit) > 1 && (
                      <Pagination
                        count={Math.ceil(totalCount / itemLimit)}
                        onChange={(_, value) => setCurrentPage(value)}
                        page={currentPage}
                        shape="rounded"
                        color="primary"
                      /> )}   
                </Grid>
                          
                <ReportQueueAdminTable
                  results={results.slice(
                    (currentPage - 1) * itemLimit,
                    currentPage * itemLimit
                  )}
                  onCancel={handleCancel}
                  deleting={deleting}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Grid>
  )
});

export default ReportQueueAdminManagement;